// LT-534 — shared helper for the "pick individual contacts" audience flow.
//
// Approach A: an explicit contact selection is materialised as an
// auto-created *static* ContactList, which is then used as the campaign's
// `contact_list_id`. This reuses the existing launch-time snapshot →
// enrolment path (services/campaigns.transition_campaign) with zero backend
// changes — the picked contacts behave exactly like any static list.

import { contactListsAPI } from '../services/contactListsAPI';
import type { ContactList } from '../types/contactList';

/** A clearly-identifiable name for an auto-created picked-contacts list so it
 * stands out on the Contact Lists page. `context` is the campaign name (or a
 * fallback). Includes a local timestamp to keep repeat picks distinct. */
export function pickedListName(context: string | undefined, count: number): string {
  const base = (context ?? '').trim() || 'Untitled campaign';
  const when = new Date().toLocaleString();
  return `${base} — picked contacts (${count}) · ${when}`;
}

/** Create a static contact list and add the given contacts to it. Returns the
 * created list so callers can select it as the campaign audience source. */
export async function createStaticListFromContacts(
  name: string,
  contactIds: string[],
): Promise<ContactList> {
  const list = await contactListsAPI.create({
    name,
    list_type: 'static',
    description: 'Auto-created from an individual-contact selection (LT-534).',
  });
  if (contactIds.length > 0) {
    await contactListsAPI.addMembers(list.id, { contact_ids: contactIds });
  }
  return list;
}
